"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { renderAccent } from "@/lib/render-accent";
import { agentUseCases } from "@/content/agent-use-cases";
import { getUseCaseContent } from "@/content/agent-use-cases/types";
import { industryPages } from "@/content/industries";

// Curated, diverse set for the homepage centerpiece — the full set lives in the directory.
const FEATURED_SLUGS = [
  "order-processing",
  "credit-evaluation",
  "invoice-matching",
  "support-triage",
  "knowledge-agent",
  "corporate-comms",
];

interface HomeUseCasesSectionProps {
  locale?: Locale;
}

export default function HomeUseCasesSection({
  locale = defaultLocale,
}: HomeUseCasesSectionProps) {
  const prefersReducedMotion = useReducedMotion();
  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  const featured = FEATURED_SLUGS.map((slug) =>
    agentUseCases.find((u) => u.structure.slug === slug),
  ).filter((u): u is (typeof agentUseCases)[number] => Boolean(u));

  const dirHref = getLocalizedPathname("/solutions/agent-use-cases/", locale);

  return (
    <section className="bg-neutral-surface py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div {...inView} className="max-w-3xl">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Use cases")}
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {renderAccent(t(locale, "What [[gets work done]] looks like"))}
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(
              locale,
              "Each of these is a real workflow we build on Claude — the manual process, the agentic redesign, and how we prove it works. Not demos.",
            )}
          </p>
        </motion.div>

        {/* Browse by industry */}
        <motion.div
          {...inView}
          className="mt-8 flex flex-wrap items-center gap-2 text-sm"
        >
          <span className="font-semibold text-neutral-emphasis">
            {t(locale, "Browse by industry:")}
          </span>
          {industryPages.map((ind) => (
            <Link
              key={ind.key}
              href={getLocalizedPathname(`/${ind.route}/`, locale)}
              className="rounded-full border border-border bg-card px-3 py-1 font-medium text-neutral-muted transition hover:border-primary hover:text-primary"
            >
              {ind.cardLabel[locale] ?? ind.cardLabel.en}
            </Link>
          ))}
        </motion.div>

        {/* Use-case cards */}
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featured.map((useCase) => {
            const c = getUseCaseContent(useCase, locale);
            const { structure } = useCase;
            return (
              <motion.article
                key={structure.slug}
                {...inView}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
              >
                {structure.cardImage && (
                  <div className="relative aspect-[16/10] overflow-hidden bg-neutral-surface">
                    <img
                      src={structure.cardImage}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      width={1536}
                      height={1024}
                      decoding="async"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-neutral-emphasis backdrop-blur">
                      {c.industry}
                    </span>
                  </div>
                )}
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                    {c.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-muted">
                    {c.cardSummary}
                  </p>
                  {structure.route && (
                    <Link
                      href={getLocalizedPathname(`/${structure.route}/`, locale)}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition hover:text-primary-emphasis"
                    >
                      {t(locale, "See how we build it")}
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* See all */}
        <motion.div {...inView} className="mt-10">
          <Link
            href={dirHref}
            data-track-event="cta_click"
            data-track-location="home_use_cases_see_all"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-base font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
          >
            {t(locale, "See all use cases")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
